import React, { FC, useCallback, useState } from 'react';
import { Link, Redirect, Route, Switch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import i18n from '../../../i18n';
import { Chip, Hidden } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { useActivate } from 'core/store/store';
import { useInitialRouterNavigation } from 'core/router/router';

import { OrderActionType } from 'state/order/actions';
import { orderActiveCountSelector, orderClosedCountSelector, orderNewCountSelector } from 'state/order/selector';

import { Stage } from 'components/Stage/Stage';
import { StageItem } from 'components/Stage/StageItem';
import { Text, TextTypography } from 'components/Text/Text';
import { Sorting, SortingField } from 'components/Sorting/Sorting';

import { OrdersActive } from './OrdersActive';
import { OrdersClosed } from './OrdersClosed';
import { OrdersNew } from './OrdersNew';

import { RoutePath } from 'RoutePath';

import { SortingDirection } from 'types';
import { OrderListSorting } from 'components/OrderList/OrderList';
import { OrderListResponseItem } from 'state/order/types';

const useStyles = makeStyles(theme => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
    },
    header: {
        padding: theme.spacing(2),
    },
    main: {
        flexGrow: 1,
        flexBasis: 0,
        position: 'relative',
    },
    tabHeaderWrapper: {
        borderBottom: `1px solid ${theme.palette.brandPrimary.accent}`,
    },
    tabHeader: {
        margin: '0 auto',
        maxWidth: 800,
        position: 'relative',
    },
    sorting: {
        padding: theme.spacing(1.75),
        position: 'absolute',
        top: 10,
        right: 0,
        zIndex: 1,
    },
    footer: {
        flexGrow: 0,
        flexBasis: 'auto',
    },
}));

const useInnerStyles = makeStyles(theme => ({
    root: {
        backgroundColor: ({ selected, to }: { selected: boolean; to: RoutePath }) => {
            if (!selected) {
                return theme.palette.brandPrimary.lightest;
            }
            switch (to) {
                case RoutePath.ORDERS_NEW:
                    return theme.palette.fixedDanger.main;
                default:
                    return theme.palette.brandSecondary.main;
            }
        },
        borderRadius: 6,
        color: ({ selected }: { selected: boolean }) => {
            return selected ? theme.palette.fixedWhite.primary : theme.palette.brandPrimary.light;
        },
        fontSize: 11,
        fontWeight: 'bold',
        height: 16,
        marginLeft: 5,
    },
    label: {
        lineHeight: '16px',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
    text: {
        color: ({ selected }: { selected: boolean }) => {
            return selected ? theme.palette.fixedPrimary.primary : theme.palette.fixedPrimary.lighter;
        },
    },
    tab: {
        display: 'inline-block',
        padding: theme.spacing(0.5),
        [theme.breakpoints.up('md')]: {
            padding: theme.spacing(1.5, 1),
        },
    },
}));

export interface OrderStageItemProps {
    to: RoutePath;
    text: string;
    total: number;
    // Optional:
    selected?: boolean;
}

const OrderStageItem: FC<OrderStageItemProps> = ({ to, text, total, selected, ...rest }) => {
    const { text: textClass, root, label, tab } = useInnerStyles({ to, selected });
    return (
        <StageItem
            component={Link}
            to={to}
            label={
                <span className={tab}>
                    <Text className={textClass} component="span" typography={TextTypography.BODY_HEADING}>
                        {text}
                    </Text>
                    {total ? <Chip classes={{ root, label }} label={total} /> : null}
                </span>
            }
            selected={selected}
            {...rest}
        />
    );
};

const toggleSorting = (sorting: SortingDirection) => {
    return sorting === SortingDirection.ASC ? SortingDirection.DESC : SortingDirection.ASC;
};

export const Orders = () => {
    useActivate(OrderActionType.ACTIVATE, OrderActionType.DEACTIVATE);
    const classes = useStyles();
    const { t } = i18n.useTranslation();
    const newOrdersCount = useSelector(orderNewCountSelector);
    const activeOrdersCount = useSelector(orderActiveCountSelector);
    const closedOrdersCount = useSelector(orderClosedCountSelector);
    const [activeTab, setActiveTab] = useInitialRouterNavigation([
        RoutePath.ORDERS_NEW,
        RoutePath.ORDERS_ACTIVE,
        RoutePath.ORDERS_CLOSED,
    ]);
    const [sorting, setSorting] = useState<OrderListSorting>({
        field: 'orderCreationDateTime',
        direction: SortingDirection.DESC,
    });
    const sortingFields: SortingField<keyof OrderListResponseItem>[] = [
        {
            name: 'orderCreationDateTime',
            label: t('orders:sorting.created'),
        },
        {
            name: 'orderNumber',
            label: t('orders:sorting.id'),
        },
        {
            name: 'orderStatus',
            label: t('orders:sorting.status'),
        },
    ];
    const handleSortingToggle = useCallback(
        (key: keyof OrderListResponseItem) => {
            setSorting({
                field: key,
                direction: sorting.field === key ? toggleSorting(sorting.direction) : SortingDirection.ASC,
            });
        },
        [sorting, setSorting],
    );
    return (
        <div className={classes.container}>
            <Hidden smUp>
                <header className={classes.header}>
                    <Text typography={TextTypography.DISPLAY_STRONG}>{t('orders:title')}</Text>
                </header>
            </Hidden>
            <main className={classes.main}>
                <div className={classes.tabHeaderWrapper}>
                    <div className={classes.tabHeader}>
                        <Hidden smDown={true}>
                            <div className={classes.sorting}>
                                <Sorting fields={sortingFields} sorting={sorting} onToggle={handleSortingToggle} />
                            </div>
                        </Hidden>
                        <Stage value={activeTab} hideDivider={true} onChange={setActiveTab}>
                            <OrderStageItem
                                to={RoutePath.ORDERS_NEW}
                                text={t('orders:tabs.new')}
                                total={newOrdersCount}
                            />
                            <OrderStageItem
                                to={RoutePath.ORDERS_ACTIVE}
                                text={t('orders:tabs.active')}
                                total={activeOrdersCount}
                            />
                            <OrderStageItem
                                to={RoutePath.ORDERS_CLOSED}
                                text={t('orders:tabs.closed')}
                                total={closedOrdersCount}
                            />
                        </Stage>
                    </div>
                </div>
                <Switch>
                    <Route path={RoutePath.ORDERS_NEW}>
                        <OrdersNew sorting={sorting} />
                    </Route>
                    <Route path={RoutePath.ORDERS_ACTIVE}>
                        <OrdersActive sorting={sorting} />
                    </Route>
                    <Route path={RoutePath.ORDERS_CLOSED}>
                        <OrdersClosed sorting={sorting} />
                    </Route>
                    <Redirect to={RoutePath.ORDERS_NEW} />
                </Switch>
            </main>
        </div>
    );
};
